// src/layouts/AdminDashboard.jsx
import React, { useState } from "react";
import {
  FaChartPie, FaUsers, FaTasks, FaTable,
  FaCog, FaChartBar, FaSignOutAlt,
  FaBars, FaTimes, FaArrowRight, FaArrowLeft
} from "react-icons/fa";

import TasksPage from "../pages/TasksPage";
import UsersPage from "../pages/UsersPage";

// import DashboardPage from "../pages/DashboardPage";
// import UsersPage from "../pages/UsersPage";
// import ReportsPage from "../pages/ReportsPage";

const menuItems = [
  {
    name: "Users",
    icon: <FaUsers />,
    page: "Users"
  },
  {
    name: "Tasks",
    icon: <FaTasks />,
    page: "Tasks"
  },
  {
    name: "Reports",
    icon: <FaTable />,
    subItems: [
      { name: "Sales", icon: <FaChartBar />, page: "SalesReport" },
      { name: "Analytics", icon: <FaChartPie />, page: "Analytics" },
      { name: "Settings", icon: <FaCog />, page: "ReportSettings" },
    ]
  },
  {
    name: "Dashboard",
    icon: <FaChartPie />,
    page: "Dashboard"
  },
  {
    name: "Logout",
    icon: <FaSignOutAlt className="text-red-500" />,
    page: "Logout",
    danger: true
  },
];

const AdminDashboard = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activePage, setActivePage] = useState("Users");
  const [collapsed, setCollapsed] = useState(false);

  const renderContent = () => {
    switch (activePage) {
      case "Users": return <div className=""><UsersPage /></div>;
      case "Tasks": return <TasksPage />;
      case "SalesReport": return <div className="p-6 md:p-10">Sales Reports</div>;
      case "Analytics": return <div className="p-6 md:p-10">Analytics Dashboard</div>;
      case "ReportSettings": return <div className="p-6 md:p-10">Report Configuration</div>;
      case "Dashboard": return <div className="p-6 md:p-10">Dashboard Overview</div>;
      case "Logout": return <div className="p-6 md:p-10">Logging out...</div>;
      default: return <div className="p-6 md:p-10">Select a section</div>;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile Hamburger */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden p-2 bg-white rounded-lg shadow-md text-gray-700 hover:bg-gray-100"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
      </button>

      {/* Sidebar – hidden on mobile unless toggled */}
      <aside
        className={`bg-white shadow-md transition-width duration-300 ease-in-out ${collapsed ? "w-14" : "w-64"
          }`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          {!collapsed && <span className="text-xl font-bold text-blue-600">
            Admin Panel
          </span>}

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
          >
            {collapsed ? <FaArrowRight /> : <FaArrowLeft />}
          </button>
        </div>


        <nav className="mt-4 px-3 space-y-1">
          {menuItems.map((item) => (
            <SidebarItem
              key={item.name}
              item={item}
              activePage={activePage}
              setActivePage={(page) => {
                setActivePage(page);
                setIsMobileOpen(false); // close on mobile after click
              }}
            />
          ))}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="min-h-screen">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

/* ───────────────────────────────────────────────
   Reusable Sidebar Item (supports submenus)
─────────────────────────────────────────────── */
const SidebarItem = ({ item, activePage, setActivePage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isActive = activePage === item.page;
  const hasSubItems = item.subItems && item.subItems.length > 0;

  return (
    <div>
      <button
        onClick={() => {
          if (hasSubItems) {
            setIsOpen(!isOpen);
          } else if (item.page) {
            setActivePage(item.page);
          }
        }}
        className={`
          group relative w-full flex items-center gap-3 px-4 py-3 rounded-lg
          text-gray-700 font-medium transition-all duration-200
          hover:bg-indigo-50 hover:text-indigo-700
          ${isActive ? "bg-indigo-100 text-indigo-700 font-semibold" : ""}
          ${item.danger ? "hover:bg-red-50 hover:text-red-700" : ""}
        `}
      >
        <span
          className={`
            text-xl transform transition-transform duration-300
            group-hover:scale-110 group-hover:rotate-6
            ${isActive ? "text-indigo-600" : "text-gray-500 group-hover:text-indigo-600"}
          `}
        >
          {item.icon}
        </span>

        <span className="flex-1 text-left truncate">{item.name}</span>

        {hasSubItems && (
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Submenu */}
      {hasSubItems && isOpen && (
        <div className="ml-6 mt-1 space-y-1 border-l-2 border-indigo-100 pl-3">
          {item.subItems.map((sub) => (
            <button
              key={sub.name}
              onClick={() => setActivePage(sub.page)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                transition-colors
                ${activePage === sub.page
                  ? "bg-indigo-50 text-indigo-700 font-medium"
                  : "text-gray-600 hover:bg-gray-100 hover:text-indigo-600"}
              `}
            >
              <span className="text-lg transform transition group-hover:scale-110">
                {sub.icon}
              </span>
              <span className="truncate">{sub.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;